import { useState, useEffect, useCallback, useRef } from 'react';
import type { TwitchAuth, TwitchContext, TwitchBitsTransaction, TwitchExtension } from './types';
import type { PubSubMessage } from '@twitch-draw/shared';

export interface TwitchState {
  auth: TwitchAuth | null;
  context: TwitchContext | null;
  isReady: boolean;
  isModerator: boolean;
  error: string | null;
}

export interface UseTwitchReturn extends TwitchState {
  useBits: (sku: string) => void;
  onTransaction: (callback: (tx: TwitchBitsTransaction) => void) => void;
  onTransactionCancelled: (callback: () => void) => void;
  onPubSubMessage: (callback: (message: PubSubMessage) => void) => void;
}

export function useTwitch(): UseTwitchReturn {
  const [state, setState] = useState<TwitchState>({
    auth: null,
    context: null,
    isReady: false,
    isModerator: false,
    error: null,
  });

  const transactionCallbackRef = useRef<((tx: TwitchBitsTransaction) => void) | null>(null);
  const transactionCancelledRef = useRef<(() => void) | null>(null);
  const pubsubCallbackRef = useRef<((message: PubSubMessage) => void) | null>(null);
  const extRef = useRef<TwitchExtension | null>(null);

  useEffect(() => {
    const ext = window.Twitch?.ext;
    if (!ext) {
      setState(s => ({ ...s, error: 'Twitch Extension Helper not loaded' }));
      return;
    }

    extRef.current = ext;

    ext.onAuthorized((auth) => {
      setState(s => ({
        ...s,
        auth,
        isReady: true,
        isModerator: ext.viewer.role === 'broadcaster' || ext.viewer.role === 'moderator',
      }));
    });

    ext.onContext((context) => {
      setState(s => ({ ...s, context }));
    });

    ext.onError((error) => {
      setState(s => ({ ...s, error: error.message }));
    });

    const pubsubHandler = (_target: string, _contentType: string, message: string) => {
      try {
        const parsed = JSON.parse(message) as PubSubMessage;
        pubsubCallbackRef.current?.(parsed);
      } catch {
        console.error('Failed to parse PubSub message');
      }
    };

    ext.listen('broadcast', pubsubHandler);

    ext.bits.onTransactionComplete((tx) => {
      transactionCallbackRef.current?.(tx);
    });

    ext.bits.onTransactionCancelled(() => {
      transactionCancelledRef.current?.();
    });

    return () => {
      ext.unlisten('broadcast', pubsubHandler);
    };
  }, []);

  const useBits = useCallback((sku: string) => {
    extRef.current?.bits.useBits(sku);
  }, []);

  const onTransaction = useCallback((callback: (tx: TwitchBitsTransaction) => void) => {
    transactionCallbackRef.current = callback;
  }, []);

  const onTransactionCancelled = useCallback((callback: () => void) => {
    transactionCancelledRef.current = callback;
  }, []);

  const onPubSubMessage = useCallback((callback: (message: PubSubMessage) => void) => {
    pubsubCallbackRef.current = callback;
  }, []);

  return {
    ...state,
    useBits,
    onTransaction,
    onTransactionCancelled,
    onPubSubMessage,
  };
}
